import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AuthService } from 'src/app/services/auth.service';
import { formatCurrency } from 'src/app/services/constants';
import {
  normalizeCreditActions,
  resolveCreditBounds,
  VoyageCreditAction,
} from 'src/app/services/operators/voyageradar-credit-actions';

/**
 * "What is a credit?", answered on the sensor preview.
 *
 * The preview prices every other sensor per sqkm, per scene or per second, and
 * Voyage Radar per credit — a unit that means nothing until you know what an
 * action costs. This is that explanation, opened from the info icon beside the
 * unit price.
 *
 * Read-only. It sells nothing and closes to nothing: the purchase happens later
 * in VoyageCreditPurchaseComponent, once a target exists and the wallet can be
 * attached to a task.
 */
@Component({
  selector: 'app-voyage-credit-info',
  templateUrl: './voyage-credit-info.component.html',
  styleUrls: ['./voyage-credit-info.component.scss'],
})
export class VoyageCreditInfoComponent implements OnInit {
  /**
   * The WHOLE catalogue, deliberately unfiltered.
   *
   * The purchase dialog narrows to the tasked shape via `actionsForTarget`,
   * because by then a target exists. Here nothing has been drawn yet, so
   * filtering would mean guessing — and guessing wrong hides an endpoint this
   * buyer may be evaluating the sensor for.
   */
  actions: VoyageCreditAction[] = [];

  unitPrice = 0;
  showPricing = false;

  minCredits = 0;
  maxCredits: number | null = null;

  private activeUser: any;

  constructor(
    private dialogRef: MatDialogRef<VoyageCreditInfoComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.authService.user.subscribe((user) => (this.activeUser = user));

    const row = this.data?.selectedRow;

    this.actions = normalizeCreditActions(row?.apiCreditActions);
    this.unitPrice = Number(row?.unitcost ?? row?.latency?.[0]?.unitcost ?? 0);
    this.showPricing = this.unitPrice > 0;

    const bounds = resolveCreditBounds(row, this.actions);
    this.minCredits = bounds.min;
    this.maxCredits = bounds.max;
  }

  /**
   * What to CALL this sensor on screen.
   *
   * `displayName` and nothing else: the caller resolved it against its own
   * `currentUser`, so an obfuscated customer already has an alias in it. The
   * fallback is a neutral noun rather than `row.name`, because `name` is the
   * REAL operator name — reaching for it when displayName is missing would leak
   * exactly what obfuscation exists to hide, and would do it only for the users
   * it is meant to protect.
   *
   * Same rule, same fallback as voyageradar.operator-strategy.ts.
   */
  get sensorName(): string {
    return this.data?.selectedRow?.displayName ?? 'This sensor';
  }

  /**
   * True when ANY entry names a shape — i.e. when there is a row this reader's
   * task might not be able to reach.
   *
   * It used to demand one 'aoi' AND one 'poi', which was the right question
   * while the catalogue tagged both refresh endpoints as a matched pair: the
   * two sat adjacent at the same price and read as a duplicate, and the tags
   * were what told them apart. `nearby` then dropped its `target`, because the
   * entry prices the /v1/vessels/nearby ENDPOINT rather than the point gesture
   * that is its only caller today (the endpoint is shaped for a second one, a
   * search around a vessel the user selects — build steps 4-8 of the
   * nearby-vessel plan, not built yet). That left `refresh`/'aoi' as the only
   * tagged entry — so the old test silently became false for the shipped
   * catalogue and took the WHOLE column with it, including the AOI badge on a
   * row that is still genuinely area-only. A pair-shaped question
   * outliving the pair is how a tag that still means something stops being
   * drawn.
   *
   * Asking "does anything carry a target" restores it, and the surviving badge
   * says more than the pair it replaces ever did. Two tags distinguished two
   * adjacent rows from each other; one tag marks the single row a point buyer
   * cannot spend on, which is the fact a buyer actually needs. The untagged
   * rows are the ones this filter never narrows away, and they say so by
   * staying blank — which is a statement about the price list, not a promise
   * that every gesture behind them exists yet. `nearby` is untagged and an
   * area task still has nothing that spends it.
   *
   * Two badges, not one, on any document that tags both shapes — and that is
   * the common case rather than the exotic one, because this modal reads the
   * LIVE operator row, which until the migration re-syncs it still carries the
   * retired `refreshPoi` with its `target: 'poi'`. Task snapshots taken before
   * the rename and ee-admin edits do the same. The single-badge description
   * above is the shipped catalogue, i.e. what this becomes, not what a buyer
   * sees today.
   *
   * Still false for a catalogue that tags nothing at all — an operator document
   * from before the split — where a column of empty cells would be noise.
   */
  get hasTargetVariants(): boolean {
    return this.actions.some((a) => !!a.target);
  }

  /** The purchase range, as the operator document states it. */
  get boundsLabel(): string {
    return this.maxCredits === null
      ? `${this.formatCredits(this.minCredits)} credits or more, per task.`
      : `${this.formatCredits(this.minCredits)} – ${this.formatCredits(this.maxCredits)} credits, per task.`;
  }

  formatCredits(n: number | null): string {
    return Number(n || 0).toLocaleString('en-US');
  }

  /** Currency or points — points-based customers are never shown dollars. */
  costLabel(cost: number): string {
    if (this.activeUser?.pointsEligible && this.activeUser?.sensorConversionFactor) {
      const [part1, part2] = this.activeUser.sensorConversionFactor.split(':').map(parseFloat);
      const conversionFactor = part2 / part1;
      return `${Math.round(cost * conversionFactor)} Points`;
    }
    return formatCurrency(cost);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
